import React, { useState, useEffect, useCallback, useMemo } from 'react';
import masterService from '../../services/masterService';
import MasterPageWrapper from './common/MasterPageWrapper';
import FormSection from './common/FormSection';
import ConfirmModal from './components/ConfirmModal';
import CommonPagination from '../../components/common/CommonPagination';
import usePagination from '../../hooks/usePagination';
import './InvestigationMasterPage.css';
import { isRadiologyDepartment } from '../../utils/disciplineHelper';

const INITIAL_DEPARTMENT = {
  subCategoryId: 0,
  categoryId: 1, // 1=Pathology, 2=Radiology
  name: '',
  displayName: '',
  abbreviation: '',
  displayPriority: 10,
  isActive: true
};

export const DepartmentMasterPage = ({ showToast }) => {
  // View Mode: 'form' | 'list'
  const [viewMode, setViewMode] = useState('form');
  const [isEditMode, setIsEditMode] = useState(false);

  // Form State
  const [formData, setFormData] = useState(INITIAL_DEPARTMENT);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // List State
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [disciplineFilter, setDisciplineFilter] = useState('all'); // 'all' | 'pathology' | 'radiology'
  const [statusFilter, setStatusFilter] = useState('all');

  // Confirmation Modal
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Load Departments for List
  const fetchDepartments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await masterService.getDepartments({
        search: searchQuery.trim() || undefined,
        isActive: statusFilter === 'all' ? undefined : statusFilter === 'active'
      });
      if (res.success && res.data) {
        setItems(res.data || []);
      }
    } catch (err) {
      showToast?.('Failed to load clinical departments.', 'error');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, showToast]);

  useEffect(() => {
    if (viewMode === 'list') {
      fetchDepartments();
    }
  }, [viewMode, fetchDepartments]);

  // Handle Form Change
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Switch to clean Form
  const handleAddNew = (disciplineOverride) => {
    const catId = disciplineOverride ? (disciplineOverride === 'RADIOLOGY' ? 2 : 1) : (disciplineFilter === 'radiology' ? 2 : 1);
    setFormData({
      ...INITIAL_DEPARTMENT,
      categoryId: catId,
      displayPriority: (items.length + 1) * 10
    });
    setErrors({});
    setIsEditMode(false);
    setViewMode('form');
  };

  // Switch to Edit Form
  const handleEditRow = (row) => {
    const isRad = isRadiologyDepartment(row);
    setFormData({
      subCategoryId: row.subCategoryId,
      categoryId: row.categoryId || (isRad ? 2 : 1),
      name: row.name || '',
      displayName: row.displayName || row.name || '',
      abbreviation: row.abbreviation || '',
      displayPriority: row.displayPriority ?? 10,
      isActive: row.isActive !== false
    });
    setErrors({});
    setIsEditMode(true);
    setViewMode('form');
  };

  // Validation
  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Department Name is required.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Form Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        categoryId: Number(formData.categoryId) || 1,
        name: formData.name.trim(),
        displayName: formData.displayName.trim() || formData.name.trim(),
        abbreviation: formData.abbreviation.trim() || null,
        displayPriority: parseInt(formData.displayPriority, 10) || 10,
        isActive: formData.isActive
      };

      let res;
      if (isEditMode) {
        res = await masterService.updateDepartment(formData.subCategoryId, payload);
      } else {
        res = await masterService.createDepartment(payload);
      }

      if (res.success) {
        showToast?.(`Department ${isEditMode ? 'updated' : 'created'} successfully!`, 'success');
        setViewMode('list');
        fetchDepartments();
      } else {
        showToast?.(res.message || 'Operation failed.', 'error');
      }
    } catch (err) {
      showToast?.('Error saving clinical department.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Status Toggle
  const handleConfirmStatus = async () => {
    if (!confirmTarget) return;
    setStatusUpdating(true);
    try {
      const newStatus = !confirmTarget.isActive;
      const res = await masterService.updateDepartmentStatus(confirmTarget.subCategoryId, newStatus);
      if (res.success) {
        showToast?.(`Department ${newStatus ? 'activated' : 'deactivated'}.`, 'success');
        setItems(prev => prev.map(x => x.subCategoryId === confirmTarget.subCategoryId ? { ...x, isActive: newStatus } : x));
      } else {
        showToast?.(res.message || 'Status update failed.', 'error');
      }
    } catch (err) {
      showToast?.('Error updating status.', 'error');
    } finally {
      setStatusUpdating(false);
      setConfirmTarget(null);
    }
  };

  // Filtered by Discipline
  const filteredItems = useMemo(() => {
    if (disciplineFilter === 'radiology') return items.filter(r => isRadiologyDepartment(r));
    if (disciplineFilter === 'pathology') return items.filter(r => !isRadiologyDepartment(r));
    return items;
  }, [items, disciplineFilter]);

  // Centralized Standardized Pagination
  const {
    page,
    pageSize,
    totalRecords,
    pagedItems,
    setPage,
    setPageSize
  } = usePagination({
    items: filteredItems,
    resetDeps: [disciplineFilter, searchQuery, statusFilter]
  });

  return (
    <MasterPageWrapper
      masterTitle={viewMode === 'form' ? (isEditMode ? 'Update Clinical Department' : 'Create Clinical Department') : 'Department Master'}
      masterName="Department"
      viewMode={viewMode}
      onToggleView={(mode) => {
        if (mode === 'form' && !isEditMode) handleAddNew();
        else setViewMode(mode);
      }}
    >
      {/* ─── FULL-SCREEN FORM VIEW ─── */}
      {viewMode === 'form' && (
        <div className="glass-card fade-in">
          <form onSubmit={handleSubmit} noValidate>
            <FormSection
              title="01. Clinical Department Information"
              iconColor="#4287f5"
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z" />
                </svg>
              }
            >
              <div className="form-grid">
                {/* Diagnostic Division / Modality Selector */}
                <div className="form-group col-span-2">
                  <label className="form-label">
                    Diagnostic Modality / Division <span className="form-req">*</span>
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px', marginTop: '4px' }}>
                    <button
                      type="button"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: formData.categoryId === 1 ? '2px solid #16a34a' : '1.5px solid #e2e8f0',
                        backgroundColor: formData.categoryId === 1 ? '#f0fdf4' : '#ffffff',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.15s ease'
                      }}
                      onClick={() => handleChange('categoryId', 1)}
                    >
                      <span style={{ fontSize: '24px' }}>🧪</span>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '14px', color: formData.categoryId === 1 ? '#15803d' : '#1e293b' }}>
                          Pathology Laboratory Department
                        </div>
                        <div style={{ fontSize: '11.5px', color: '#64748b' }}>
                          Biochemistry, Hematology, Microbiology, Serology, Histology
                        </div>
                      </div>
                      {formData.categoryId === 1 && (
                        <span style={{ marginLeft: 'auto', fontSize: '11px', fontWeight: 700, color: '#15803d', background: '#dcfce7', padding: '2px 6px', borderRadius: '4px' }}>
                          ✓ Active
                        </span>
                      )}
                    </button>

                    <button
                      type="button"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: formData.categoryId === 2 ? '2px solid #2563eb' : '1.5px solid #e2e8f0',
                        backgroundColor: formData.categoryId === 2 ? '#eff6ff' : '#ffffff',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.15s ease'
                      }}
                      onClick={() => handleChange('categoryId', 2)}
                    >
                      <span style={{ fontSize: '24px' }}>☢️</span>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '14px', color: formData.categoryId === 2 ? '#1d4ed8' : '#1e293b' }}>
                          Radiology Imaging Department
                        </div>
                        <div style={{ fontSize: '11.5px', color: '#64748b' }}>
                          X-Ray, Ultrasound, CT Scan, MRI Scan, Mammography, Imaging
                        </div>
                      </div>
                      {formData.categoryId === 2 && (
                        <span style={{ marginLeft: 'auto', fontSize: '11px', fontWeight: 700, color: '#1d4ed8', background: '#dbeafe', padding: '2px 6px', borderRadius: '4px' }}>
                          ✓ Active
                        </span>
                      )}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Department Name <span className="form-req">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-input ${errors.name ? 'border-danger' : ''}`}
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder={formData.categoryId === 2 ? "e.g. MRI SCAN, CT SCAN, X-RAY" : "e.g. Clinical Biochemistry, Hematology"}
                    required
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Display Name / Reporting Alias</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.displayName}
                    onChange={(e) => handleChange('displayName', e.target.value)}
                    placeholder="Printed on patient diagnostic report headers"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Abbreviation / Code</label>
                  <input
                    type="text"
                    className="form-input"
                    style={{ textTransform: 'uppercase', fontFamily: 'monospace' }}
                    value={formData.abbreviation}
                    onChange={(e) => handleChange('abbreviation', e.target.value.toUpperCase())}
                    placeholder="e.g. BIO, HEM, MICRO, XRAY, MRI"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Report Print Sequence Priority</label>
                  <input
                    type="number"
                    min="1"
                    className="form-input"
                    value={formData.displayPriority}
                    onChange={(e) => handleChange('displayPriority', e.target.value)}
                  />
                </div>

                <div className="form-group col-span-2">
                  <label className="checkbox-label" style={{ marginTop: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => handleChange('isActive', e.target.checked)}
                    />
                    <span><strong>Department Active</strong> (Available for investigation linking and sample routing)</span>
                  </label>
                </div>
              </div>
            </FormSection>

            {/* Form Footer */}
            <div className="form-actions-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setViewMode('list')}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
                style={{ minWidth: '140px' }}
              >
                {saving ? 'Saving...' : (isEditMode ? 'Update Department' : 'Save Department')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ─── FULL-SCREEN GRID / LIST VIEW ─── */}
      {viewMode === 'list' && (
        <div className="glass-card fade-in" style={{ padding: '1.5rem' }}>
          {/* Top Modality / Discipline Filter Tabs */}
          <div className="discipline-filter-tabs">
            <button
              type="button"
              className={`discipline-filter-btn ${disciplineFilter === 'all' ? 'active' : ''}`}
              onClick={() => { setDisciplineFilter('all'); setPage(1); }}
            >
              <span>🌐 All Departments</span>
              <span style={{ opacity: 0.8, fontSize: '11px' }}>({(items || []).length})</span>
            </button>
            <button
              type="button"
              className={`discipline-filter-btn pathology ${disciplineFilter === 'pathology' ? 'active' : ''}`}
              onClick={() => { setDisciplineFilter('pathology'); setPage(1); }}
            >
              <span>🧪 Pathology Laboratory Depts</span>
            </button>
            <button
              type="button"
              className={`discipline-filter-btn radiology ${disciplineFilter === 'radiology' ? 'active' : ''}`}
              onClick={() => { setDisciplineFilter('radiology'); setPage(1); }}
            >
              <span>☢️ Radiology Imaging Modalities</span>
            </button>
          </div>

          {/* Filter Bar */}
          <div className="filter-bar">
            <div className="filter-bar-left">
              <div style={{ position: 'relative', width: '320px', minWidth: '240px' }}>
                <input
                  type="text"
                  className="form-input"
                  style={{ paddingLeft: '2.4rem' }}
                  placeholder="Search department name or abbreviation..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                />
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>

              <select
                className="form-select"
                style={{ width: '150px' }}
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              >
                <option value="all">All Statuses</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
            </div>

            <div className="filter-bar-right">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => handleAddNew()}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>
                  {disciplineFilter === 'radiology' ? '+ Add Radiology Modality' : (disciplineFilter === 'pathology' ? '+ Add Pathology Dept' : '+ Add Department')}
                </span>
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="master-table mvc-table">
              <thead>
                <tr>
                  <th style={{ width: '60px', textAlign: 'center' }}>Sr.</th>
                  <th style={{ width: '120px' }}>Code / Abbr</th>
                  <th>Department Name & Division</th>
                  <th>Print Display Alias</th>
                  <th style={{ width: '130px', textAlign: 'center' }}>Report Sequence</th>
                  <th style={{ width: '100px', textAlign: 'center' }}>Status</th>
                  <th style={{ width: '140px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      Loading clinical departments...
                    </td>
                  </tr>
                ) : pagedItems.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      No departments matching your criteria.
                    </td>
                  </tr>
                ) : (
                  pagedItems.map((r, index) => {
                    const isRad = isRadiologyDepartment(r);
                    return (
                      <tr key={r.subCategoryId || index}>
                        <td style={{ textAlign: 'center', fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                          {(page - 1) * pageSize + index + 1}
                        </td>
                        <td style={{ fontFamily: 'monospace', fontWeight: 600, color: isRad ? '#2563eb' : 'var(--accent)' }}>
                          {r.abbreviation || `DEP_${r.subCategoryId}`}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{r.name}</span>
                            <span className={`discipline-badge ${isRad ? 'radiology' : 'pathology'}`}>
                              {isRad ? '☢️ Radiology' : '🧪 Pathology'}
                            </span>
                          </div>
                        </td>
                      <td style={{ color: 'var(--text-secondary)' }}>
                        {r.displayName || r.name}
                      </td>
                      <td style={{ textAlign: 'center', fontFamily: 'monospace' }}>
                        {r.displayPriority ?? 10}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge-status ${r.isActive ? 'active' : 'inactive'}`}>
                          {r.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ padding: '3px 8px', fontSize: '11.5px' }}
                            onClick={() => handleEditRow(r)}
                            title="Edit Department"
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{
                              padding: '3px 8px',
                              fontSize: '11.5px',
                              color: r.isActive ? '#ef4444' : '#10b981'
                            }}
                            onClick={() => setConfirmTarget(r)}
                            title={r.isActive ? 'Deactivate Department' : 'Activate Department'}
                          >
                            {r.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
              </tbody>
            </table>
          </div>

          {/* Standardized LIMS Centralized Pagination */}
          <CommonPagination
            page={page}
            pageSize={pageSize}
            totalRecords={totalRecords}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            loading={loading}
          />
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={!!confirmTarget}
        title={confirmTarget?.isActive ? 'Deactivate Department' : 'Activate Department'}
        message={
          confirmTarget?.isActive
            ? `Are you sure you want to deactivate "${confirmTarget?.name}"?`
            : `Are you sure you want to activate "${confirmTarget?.name}"?`
        }
        confirmLabel={confirmTarget?.isActive ? 'Deactivate' : 'Activate'}
        variant={confirmTarget?.isActive ? 'danger' : 'primary'}
        loading={statusUpdating}
        onConfirm={handleConfirmStatus}
        onCancel={() => setConfirmTarget(null)}
      />
    </MasterPageWrapper>
  );
};

export default DepartmentMasterPage;
